<!--
Dashboard
-->
<template>
  <main class="dashboard-grid">
    <!-- StatsCard -->
    <el-card
      shadow="never"
      class="small-stats-card dashboard-card"
      :body-style="{
        display: 'flex',
        flexDirection: 'row',
        height: '100%',
        justifyContent: 'space-between',
        padding: '5px 20px',
        alignItems: 'center',
      }">
      <StatsElement
        v-for="s in statsCardData"
        :key="s.i18nKey"
        :display-i18n-key="s.i18nKey"
        :display-number="s.displayNumber"
        :is-price="s.isPrice"
        clickable
        :color="s.color"
        :icon="s.icon"
        @click="s.callback" />
    </el-card>
    <div class="line-chart-card dashboard-card">
      <InformationChartCard />
    </div>
    <ActivityCard class="last-activity-card dashboard-card" />
    <div class="optimizations-card dashboard-card h-full">
      <OptimizationsCard />
    </div>
  </main>
</template>

<script lang="ts" setup>
  import ActivityCard from '@/components/activities/ActivityCard.vue'
  import OptimizationsCard from '@/components/optimizations/OptimizationsCard.vue'
  import { DashboardService, ResourceCounts, LicenceFilter } from '@/client'

  import { useRouter } from 'vue-router'
  import { usePreferenceStore } from '@/stores/preferenceStore'
  import { File, LayoutGrid, UserRound, Wallet } from 'lucide-vue-next'
  import { ApplicationStore } from '@/stores/application/application.store'
  import { AccountStore } from '@/stores/account/account.store'

  type Stat = {
    i18nKey: string
    icon: Component
    displayNumber: number | string | undefined | null
    color: string
    isPrice: boolean
    callback: () => unknown
  }

  const preferenceStore = usePreferenceStore()
  const router = useRouter()

  const countStats = ref<ResourceCounts>()
  const statsCardData = computed<Stat[]>(() => {
    return [
      {
        i18nKey: 'managedUsers',
        icon: UserRound,
        displayNumber: countStats.value?.accounts,
        isPrice: false,
        color: 'orange',
        callback: () => router.push({ path: '/users' }),
      },
      {
        i18nKey: 'managedLicenses',
        icon: File,
        displayNumber: countStats.value?.licences,
        isPrice: false,
        color: 'red',
        callback: () => router.push({ path: '/accounts' }),
      },
      {
        i18nKey: 'applications',
        icon: LayoutGrid,
        displayNumber: convertedApplications.value,
        isPrice: false,
        color: 'purple',
        callback: () => router.push({ path: '/applications' }),
      },
      {
        i18nKey: 'yearlySaasSpends',
        icon: Wallet,
        displayNumber: costs.value,
        isPrice: true,
        color: 'blue',
        callback: () => router.push({ path: '/applications' }),
      },
    ]
  })

  const convertedApplications = computed(() => {
    if (countStats.value) {
      return (
        countStats.value?.software +
        countStats.value?.sso_apps_discovered -
        countStats.value?.sso_providers
      )
    } else {
      return null
    }
  })

  onMounted(async () => {
    countStats.value =
      await DashboardService.getResourceCountsApiV1DashboardResourceCountsGet({
        licenceFilter: LicenceFilter.ALL,
      })

    AccountStore.totalAccountCount.value = countStats.value.licences
  })

  const { totalAppCosts: totalCosts } = ApplicationStore.useApplicationsCosts()

  const costs = computed(() =>
    totalCosts.value != null ? totalCosts.value * 12 : null
  )

  watch(
    () => preferenceStore.preferences?.currency_code,
    async () => {
      totalCosts.value = undefined
      ApplicationStore.updateApplicationCostsCacheFromApi()
    },
    { immediate: false }
  )
</script>

<style scoped>
  /* Header */
  .welcome-message {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .dashboard-grid {
    height: 100%;
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1.2fr;
    grid-template-rows: repeat(8, minmax(80px, 1fr));
    gap: 1.5rem;
    grid-template-areas:
      'small-stats-card small-stats-card small-stats-card small-stats-card'
      'line-chart-card line-chart-card line-chart-card last-activity-card'
      'line-chart-card line-chart-card line-chart-card last-activity-card'
      'line-chart-card line-chart-card line-chart-card last-activity-card'
      'line-chart-card line-chart-card line-chart-card last-activity-card'
      'optimizations-card optimizations-card optimizations-card last-activity-card'
      'optimizations-card optimizations-card optimizations-card last-activity-card'
      'optimizations-card optimizations-card optimizations-card last-activity-card';
  }

  .small-stats-card {
    grid-area: small-stats-card;
    height: 100%;
  }

  .line-chart-card {
    grid-area: line-chart-card;
  }

  .savings-card {
    grid-area: savings-card;
  }

  .last-activity-card {
    grid-area: last-activity-card;
    height: 100%;
  }

  .optimizations-card {
    grid-area: optimizations-card;
    height: 100%;
  }
</style>

<style lang="scss">
  .dashboard-grid {
    .dashboard-card.el-card {
      border-radius: var(--border-radius) !important;
      -webkit-box-shadow: var(--shadow-wide);
      box-shadow: var(--shadow-wide);
      box-sizing: content-box;
    }
  }
</style>
